import { useRef, useState } from 'react';

interface UseInputBufferOptions {
  /**
   * When true, a blank field snaps back to the stored value on blur instead of
   * committing the empty value. Use for fields where an empty value is
   * meaningless (e.g. numeric inputs that would parse '' to 0). Defaults to
   * false — a blank field commits '', letting validation flag a required field.
   */
  snapBackOnEmpty?: boolean;
  /**
   * When true, commit each non-blank change immediately instead of on blur. Use
   * for inputs that change without blurring — notably a number input's native
   * stepper arrows, which fire change but keep focus. Defaults to false.
   */
  commitOnChange?: boolean;
  /**
   * Optional real-time validator run against the buffer on every change and on
   * blur. Return an error message for an invalid value, or '' / undefined when
   * valid; the message is exposed as `error` for the field to render. Omit it
   * for no validation. This validates the TYPED value, independent of whether
   * the value has been committed to the model.
   */
  validate?: (inputValue: string) => string | undefined;
}

/**
 * Backs a controlled text input with a local string buffer and commits on blur,
 * so the model isn't rewritten mid-edit and renders/validation stay off the
 * typing path. The field keeps showing the buffer, so it stays clearable. See
 * UseInputBufferOptions for empty-handling, commit-on-change, and validation.
 * This hook does not read validation from the model — it only decouples the
 * typed text from it.
 *
 * @param committedValue current model value; `null`/`undefined` shows as empty
 * @param onChange called with the entered input value
 * @param options see UseInputBufferOptions
 */
const useInputBuffer = <T>(
  committedValue: T,
  onChange: (inputValue: string) => void,
  options: UseInputBufferOptions = {}
) => {
  const { snapBackOnEmpty = false, commitOnChange = false, validate } = options;
  const [buffer, setBuffer] = useState<string | undefined>(undefined);
  const [error, setError] = useState('');
  // handleBlur reads this ref, not `buffer`, so a same-tick change+blur (e.g. a
  // stepper click that moves focus) commits the latest value, not a stale one.
  const bufferRef = useRef<string | undefined>(undefined);

  const committedString = committedValue == null ? '' : String(committedValue);

  const runValidate = (value: string) => {
    if (validate) {
      setError(validate(value) ?? '');
    }
  };

  const setBufferValue = (value: string | undefined) => {
    bufferRef.current = value;
    setBuffer(value);
  };

  const handleChange = (inputValue: string) => {
    setBufferValue(inputValue);
    runValidate(inputValue);
    // Blank is left for blur, where snapBackOnEmpty decides commit vs revert.
    if (commitOnChange && inputValue.trim() !== '') {
      onChange(inputValue);
    }
  };

  const handleBlur = () => {
    const current = bufferRef.current;
    const snappingBack = snapBackOnEmpty && current?.trim() === '';
    // Non-blank values already committed on change when commitOnChange is set.
    const alreadyCommitted = commitOnChange && current?.trim() !== '';
    if (current !== undefined && !snappingBack && !alreadyCommitted) {
      onChange(current);
    }
    // Validate what the field settles on: committed value if snapping back, else buffer.
    runValidate(snappingBack ? committedString : current ?? committedString);
    setBufferValue(undefined);
  };

  return {
    value: buffer ?? committedString,
    error,
    handleChange,
    handleBlur,
  };
};

export default useInputBuffer;
